import React, { useState, useEffect, useRef } from "react";
import { motion, MotionConfig, useMotionValue, useInView, animate } from "framer-motion";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import {
  Menu,
  Phone,
  ArrowRight,
  Star,
  Users,
  Clock,
  Award,
  GraduationCap,
  ChevronRight,
  ChevronDown,
  MessageCircle,
  MapPin,
  Mail,
  Wand2,
  Activity,
  Droplets,
  BadgeCheck,
  Leaf,
  HeartHandshake
} from "lucide-react";
import RhinoplastyPage from "@/pages/RhinoplastyPage";
import ChinAugmentationPage from "@/pages/ChinAugmentationPage";
import CheekAugmentationPage from "@/pages/CheekAugmentationPage";
import BreastAugmentationPage from "@/pages/BreastAugmentationPage";
import BreastLiftPage from "@/pages/BreastLiftPage";
import BreastReductionPage from "@/pages/BreastReductionPage";
import BreastFatTransferPage from "@/pages/BreastFatTransferPage";
import BreastImplantRemovalPage from "@/pages/BreastImplantRemovalPage";
import FibroadenomaPage from "@/pages/FibroadenomaPage";
import LipomaRemovalPage from "@/pages/LipomaRemovalPage";
import SebaceousCystRemovalPage from "@/pages/SebaceousCystRemovalPage";
import MoleRemovalPage from "@/pages/MoleRemovalPage";
import CornRemovalPage from "@/pages/CornRemovalPage";
import KeloidTreatmentPage from "@/pages/KeloidTreatmentPage";
import SkinTagRemovalPage from "@/pages/SkinTagRemovalPage";
import WartRemovalPage from "@/pages/WartRemovalPage";
import NailRemovalSurgeryPage from "@/pages/NailRemovalSurgeryPage";
import NeurofibromaRemovalPage from "@/pages/NeurofibromaRemovalPage";
import AxillaryBreastRemovalPage from "@/pages/AxillaryBreastRemovalPage";
import MaleNippleReductionPage from "@/pages/MaleNippleReductionPage";
import InvertedNippleCorrectionPage from "@/pages/InvertedNippleCorrectionPage";
import LiposuctionPage from "@/pages/LiposuctionPage";
import TummyTuckPage from "@/pages/TummyTuckPage";
import ArmLiftPage from "@/pages/ArmLiftPage";
import ThighLiftPage from "@/pages/ThighLiftPage";
import ButtockAugmentationPage from "@/pages/ButtockAugmentationPage";
import BodyLiftPage from "@/pages/BodyLiftPage";
import FaceliftPage from "@/pages/FaceliftPage";
import BlepharoplastyPage from "@/pages/BlepharoplastyPage";
import ThreadLiftPage from "@/pages/ThreadLiftPage";
import DimpleplastyPage from "@/pages/DimpleplastyPage";
import BuccalFatRemovalPage from "@/pages/BuccalFatRemovalPage";
import NeckLiftPage from "@/pages/NeckLiftPage";
import DoubleChinRemovalPage from "@/pages/DoubleChinRemovalPage";
import OtoplastyPage from "@/pages/OtoplastyPage";
import EarlobeRepairPage from "@/pages/EarlobeRepairPage";
import GynecomastiaPage from "@/pages/GynecomastiaPage";
import MommyMakeoverPage from "@/pages/MommyMakeoverPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import CosmeticSurgeryPage from "@/pages/CosmeticSurgeryPage";
import ReconstructiveSurgeryPage from "@/pages/ReconstructiveSurgeryPage";
import NonSurgicalPage from "@/pages/NonSurgicalPage";
import CleftLipPalatePage from "@/pages/reconstructive/CleftLipPalatePage";
import CraniosynostosisPage from "@/pages/reconstructive/CraniosynostosisPage";
import EarDeformitiesPage from "@/pages/reconstructive/EarDeformitiesPage";
import BurnReconstructionPage from "@/pages/reconstructive/BurnReconstructionPage";
import ScarManagementPage from "@/pages/reconstructive/ScarManagementPage";
import SkinGraftingPage from "@/pages/reconstructive/SkinGraftingPage";
import PostBurnContracturePage from "@/pages/reconstructive/PostBurnContracturePage";
import HypertrophicScarsPage from "@/pages/reconstructive/HypertrophicScarsPage";
import LimbSalvagePage from "@/pages/reconstructive/LimbSalvagePage";
import ReplantationPage from "@/pages/reconstructive/ReplantationPage";
import DiabeticFootPage from "@/pages/reconstructive/DiabeticFootPage";
import MigraineSurgeryPage from "@/pages/reconstructive/MigraineSurgeryPage";
import BotoxTreatmentPage from "@/pages/non-surgical/BotoxTreatmentPage";
import DermalFillersPage from "@/pages/non-surgical/DermalFillersPage";
import LiquidRhinoplastyPage from "@/pages/non-surgical/LiquidRhinoplastyPage";
import ChemicalPeelPage from "@/pages/non-surgical/ChemicalPeelPage";
import PRPTherapyPage from "@/pages/non-surgical/PRPTherapyPage";
import SpiderVeinPage from "@/pages/non-surgical/SpiderVeinPage";
import HairTransplantPage from "@/pages/non-surgical/HairTransplantPage";
import BeardTransplantPage from "@/pages/non-surgical/BeardTransplantPage";
import HairFallHairLossPage from "@/pages/HairFallHairLossPage";
import ScalpReductionPage from "@/pages/non-surgical/ScalpReductionPage";
import BlogPage from "@/pages/BlogPage";
import BlogArticlePage from "@/pages/BlogArticlePage";
import blogPosts from "@/data/blogPosts";
import NotFoundPage from "@/pages/NotFoundPage";
import SEO from "@/components/SEO";
import { FOOTER_PATIENTS_BADGE, PATIENTS_STAT } from "@/constants/practiceStats";
import {
  PHONE_E164 as PHONE_NUMBER,
  WHATSAPP_URL as WHATSAPP_LINK,
  TEL_URL as PHONE_LINK,
  GOOGLE_MAPS_URL,
  GOOGLE_BUSINESS_RATING,
  CLINIC_ADDRESS_DISPLAY,
  CLINIC_ADDRESS_STREET,
} from "@/constants/contact";
import { ALL_TESTIMONIALS, initialsFromDisplayName } from "@/constants/testimonials";
import { TestimonialScrollRow } from "@/components/TestimonialScrollRow";
import { GoogleGMark } from "@/components/GoogleGMark";

const FOOTER_NAV_COLUMNS = [
  {
    heading: "Face Procedures",
    links: [
      { name: "Facelift Surgery", href: "/facelift-surgery-mumbai" },
      { name: "Rhinoplasty (Nose Surgery)", href: "/rhinoplasty-nose-surgery-mumbai" },
      { name: "Cheek Augmentation", href: "/cheek-augmentation-in-mumbai" },
      { name: "Chin Augmentation", href: "/chin-augmentation-in-mumbai" },
      { name: "Eyelid Surgery", href: "/blepharoplasty-eyelid-surgery-mumbai" },
      { name: "Ear Reshaping", href: "/otoplasty-ear-reshaping-surgery-mumbai" },
      { name: "Neck Lift", href: "/neck-lift-surgery-mumbai" },
      { name: "Thread Lift", href: "/thread-lift-mumbai" },
      { name: "Dimple Creation", href: "/dimple-creation-surgery-mumbai" },
      { name: "Buccal Fat Removal", href: "/buccal-fat-removal-mumbai" },
      { name: "Double Chin Removal", href: "/double-chin-removal-mumbai" },
    ],
  },
  {
    heading: "Body Contouring",
    links: [
      { name: "Liposuction", href: "/liposuction-surgery-mumbai" },
      { name: "Tummy Tuck", href: "/tummy-tuck-surgery-mumbai" },
      { name: "Arm Lift", href: "/arm-lift-surgery-mumbai" },
      { name: "Thigh Lift", href: "/thigh-lift-surgery-mumbai" },
      { name: "Buttock Augmentation", href: "/buttock-augmentation-surgery-mumbai" },
      { name: "Body Lift", href: "/body-lift-surgery-mumbai" },
      { name: "Gynecomastia", href: "/gynecomastia-surgery-mumbai" },
      { name: "Mommy Makeover", href: "/mommy-makeover-surgery-mumbai" },
    ],
  },
  {
    heading: "Breast Surgery",
    links: [
      { name: "Breast Augmentation", href: "/breast-implant-surgery-mumbai" },
      { name: "Breast Lift", href: "/breast-lift-surgery-mumbai" },
      { name: "Breast Reduction", href: "/breast-reduction-surgery-mumbai" },
      { name: "Breast Fat Transfer", href: "/breast-fat-transfer-lipofilling-mumbai" },
      { name: "Breast Implant Removal", href: "/breast-implant-removal-surgery-mumbai" },
      { name: "Nipple Correction", href: "/inverted-nipple-correction-mumbai" },
      { name: "Axillary Breast Removal", href: "/axillary-breast-removal-mumbai" },
      { name: "Fibroadenoma Treatment", href: "/fibroadenoma-breast-lump-treatment-mumbai" },
    ],
  },
  {
    heading: "Hair & Skin",
    links: [
      { name: "Hair Transplant", href: "/hair-transplant-mumbai" },
      { name: "Beard Transplant", href: "/beard-transplant-mumbai" },
      { name: "PRP Hair Therapy", href: "/prp-therapy-hair-loss-mumbai" },
      { name: "Hair Fall Treatment", href: "/hair-fall-hair-loss-treatment-mumbai" },
      { name: "Botox", href: "/botox-treatment-mumbai" },
      { name: "Dermal Fillers", href: "/dermal-fillers-mumbai" },
      { name: "Chemical Peels", href: "/chemical-peels-mumbai" },
      { name: "Scar Management", href: "/scar-management-treatment-mumbai" },
    ],
  },
  {
    heading: "Minor Surgical",
    links: [
      { name: "Lipoma Removal", href: "/lipoma-removal-surgery-mumbai" },
      { name: "Sebaceous Cyst Removal", href: "/sebaceous-cyst-removal-mumbai" },
      { name: "Mole Removal", href: "/mole-removal-mumbai" },
      { name: "Skin Tag Removal", href: "/skin-tag-removal-mumbai" },
      { name: "Wart Removal", href: "/wart-removal-mumbai" },
      { name: "Corn Removal", href: "/corn-removal-treatment-mumbai" },
      { name: "Keloid Treatment", href: "/keloid-treatment-mumbai" },
      { name: "Burns & Burn Scars", href: "/burns-burn-scar-treatment-mumbai" },
    ],
  },
];

// Top navigation — mirrors footer columns exactly
const NAV_DROPDOWNS = FOOTER_NAV_COLUMNS.map((col) => ({
  id: col.heading.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z-]/g, ""),
  label: col.heading,
  items: col.links,
}));

/** Pathnames that should highlight a parent category (legacy URLs after redirects). */
const NAV_CATEGORY_ACTIVE_EXTRAS = {
  "face-procedures": ["/rhinoplasty-in-mumbai", "/cheek-augmentation-in-mumbai", "/chin-augmentation-in-mumbai"],
  "body-contouring": ["/liposuction-in-mumbai"],
  "breast-surgery": ["/nipple-correction-surgery-mumbai", "/nipple-correction-surgery-in-mumbai"],
  "hair-skin": [],
  "minor-surgical": [],
};

function pathnameMatchesNavCategory(pathname, categoryId, items) {
  const hrefSet = new Set(items.map((i) => i.href));
  if (hrefSet.has(pathname)) return true;
  const extras = NAV_CATEGORY_ACTIVE_EXTRAS[categoryId] || [];
  return extras.includes(pathname);
}

// Stats Data
const stats = [
  { number: "17+", countTo: 17, suffix: "+", label: "Years in Practice" },
  { number: "55+", countTo: 55, suffix: "+", label: "Procedures" },
  { ...PATIENTS_STAT, countTo: 20000, suffix: "+", formatFn: (n) => n.toLocaleString('en-IN') },
  {
    number: `${GOOGLE_BUSINESS_RATING}★`,
    countTo: parseFloat(GOOGLE_BUSINESS_RATING),
    suffix: "★",
    decimals: 1,
    label: "Rated on Google",
    labelNormalCase: true,
    href: GOOGLE_MAPS_URL,
  },
];

// Services Data
const services = [
  {
    title: "Cosmetic Surgery",
    description: "Enhance your natural beauty with precision surgical procedures designed to bring out your best features while maintaining authenticity.",
    icon: Wand2,
    link: "/cosmetic-surgery-in-mumbai",
    isRoute: true,
  },
  {
    title: "Reconstructive Surgery",
    description: "Restore form and function with advanced reconstructive techniques. From trauma repair to complex reconstructions.",
    icon: Activity,
    link: "/reconstructive-surgery-in-mumbai",
    isRoute: true,
  },
  {
    title: "Non-Surgical Treatments",
    description: "Achieve remarkable results without surgery. Botox, fillers, laser treatments, and cutting-edge aesthetic procedures.",
    icon: Droplets,
    link: "/non-surgical-treatments-in-mumbai",
    isRoute: true,
  },
];

// Why Us Data
const whyUsPoints = [
  {
    title: "Dual Expertise",
    description: "Board-certified in both cosmetic and reconstructive surgery, offering comprehensive care under one roof.",
    icon: BadgeCheck,
  },
  {
    title: "Natural Results",
    description: "Philosophy centered on enhancing natural beauty, not changing who you are. Subtle, elegant transformations.",
    icon: Leaf,
  },
  {
    title: "International Training",
    description: "Advanced training from world-renowned institutions in Europe. Global expertise, local care.",
    icon: GraduationCap,
  },
  {
    title: "Full-Spectrum Care",
    description: "From initial consultation through recovery, personalized attention at every step of your journey.",
    icon: HeartHandshake,
  },
];

// Testimonials (shared with procedure pages — see constants/testimonials.js)
const testimonials = ALL_TESTIMONIALS.map((t) => ({
  id: t.id,
  name: t.name,
  procedure: t.procedure,
  rating: t.rating,
  quote: t.text,
  initials: initialsFromDisplayName(t.name),
}));

// Navbar Component
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { pathname } = useLocation();

  const navBase =
    "nav-link text-xs font-medium tracking-wider uppercase whitespace-nowrap";
  const linkClass = (active) =>
    cn(navBase, active ? "text-gold nav-link-active" : "text-white/85 hover:text-gold");

  const mobileLinkClass = (active) =>
    cn(
      "text-lg font-medium transition-colors",
      active ? "text-gold" : "text-deep-navy hover:text-gold"
    );

  return (
    <nav className="navbar-sticky" data-testid="navbar">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl min-w-0">
        <div className="flex items-center justify-between gap-3 lg:gap-4 min-h-20 h-auto py-3 w-full min-w-0">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center shrink-0 min-w-0"
            data-testid="logo"
          >
            <span className="font-serif text-xl md:text-2xl text-white tracking-tight truncate">
              Dr. Vicky Jain
            </span>
          </Link>

          {/* Desktop: nav + CTA stay in viewport (wrap / tighter gaps; min-w-0 avoids page-wide horizontal scroll) */}
          <div className="hidden lg:flex flex-wrap items-center justify-end gap-x-3 xl:gap-4 gap-y-2 min-w-0 flex-1">
            <Link
              to="/"
              className={linkClass(pathname === "/")}
              data-testid="nav-home"
            >
              Home
            </Link>
            {NAV_DROPDOWNS.map((cat) => {
              const categoryActive = pathnameMatchesNavCategory(
                pathname,
                cat.id,
                cat.items
              );
              return (
                <DropdownMenu key={cat.id}>
                  <DropdownMenuTrigger
                    className={cn(
                      navBase,
                      "inline-flex items-center gap-0.5 border-0 bg-transparent p-0 cursor-pointer outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-premium-purple shrink-0",
                      categoryActive
                        ? "text-gold nav-link-active"
                        : "text-white/85 hover:text-gold"
                    )}
                    data-testid={`nav-${cat.id}`}
                  >
                    {cat.label}
                    <ChevronDown className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    sideOffset={8}
                    collisionPadding={16}
                    className="z-[200] min-w-[17rem] max-w-[min(17rem,calc(100vw-2rem))] max-h-[min(70vh,520px)] overflow-y-auto overflow-x-hidden rounded-none border border-deep-navy/10 bg-warm-white p-1 text-deep-navy shadow-md"
                  >
                    {cat.items.map((item) => (
                      <DropdownMenuItem
                        key={`${item.href}-${item.name}`}
                        asChild
                        className="rounded-none px-3 py-2 text-sm focus:bg-gold/10 focus:text-deep-navy cursor-pointer whitespace-normal"
                      >
                        <Link to={item.href}>{item.name}</Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })}
            <Link
              to="/about"
              className={linkClass(pathname === "/about")}
              data-testid="nav-about"
            >
              About
            </Link>
            <Link
              to="/contact"
              className={linkClass(pathname === "/contact")}
              data-testid="nav-contact"
            >
              Contact
            </Link>
            <Link
              to="/blog"
              className={linkClass(pathname.startsWith("/blog"))}
              data-testid="nav-blog"
            >
              Blog
            </Link>
          </div>

          {/* Mobile Menu */}
          <div className="lg:hidden">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
                  <Menu className="h-6 w-6 text-gold" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="bg-warm-white w-80 flex flex-col h-full p-6"
              >
                <SheetTitle className="font-serif text-deep-navy text-xl mb-4 shrink-0">
                  Menu
                </SheetTitle>
                <div className="flex flex-col flex-1 min-h-0 overflow-y-auto">
                  <Link
                    to="/"
                    className={cn(mobileLinkClass(pathname === "/"), "mb-2")}
                    onClick={() => setIsOpen(false)}
                    data-testid="mobile-nav-home"
                  >
                    Home
                  </Link>
                  <Accordion type="single" collapsible className="w-full border-0">
                    {NAV_DROPDOWNS.map((cat) => (
                      <AccordionItem
                        key={cat.id}
                        value={cat.id}
                        className="border-deep-navy/10"
                      >
                        <AccordionTrigger
                          className="text-lg font-medium text-deep-navy hover:no-underline py-3 [&>svg]:text-deep-navy"
                          data-testid={`mobile-nav-${cat.id}`}
                        >
                          {cat.label}
                        </AccordionTrigger>
                        <AccordionContent>
                          <ul className="flex flex-col gap-2 pl-1 pb-2">
                            {cat.items.map((item) => (
                              <li key={`${item.href}-${item.name}`}>
                                <Link
                                  to={item.href}
                                  className="text-base text-deep-navy/80 hover:text-gold transition-colors"
                                  onClick={() => setIsOpen(false)}
                                >
                                  {item.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                  <Link
                    to="/about"
                    className={cn(mobileLinkClass(pathname === "/about"), "mt-2")}
                    onClick={() => setIsOpen(false)}
                    data-testid="mobile-nav-about"
                  >
                    About
                  </Link>
                  <Link
                    to="/contact"
                    className={cn(mobileLinkClass(pathname === "/contact"), "mt-2")}
                    onClick={() => setIsOpen(false)}
                    data-testid="mobile-nav-contact"
                  >
                    Contact
                  </Link>
                  <Link
                    to="/blog"
                    className={cn(mobileLinkClass(pathname.startsWith("/blog")), "mt-2")}
                    onClick={() => setIsOpen(false)}
                    data-testid="mobile-nav-blog"
                  >
                    Blog
                  </Link>
                </div>
                <div className="shrink-0 pt-6 mt-4 border-t border-deep-navy/10">
                  <Button
                    asChild
                    className="w-full btn-gold border-none rounded-none"
                    data-testid="mobile-book-consultation"
                  >
                    <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                      Book Consultation
                    </a>
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Animated counter — counts from 0 to target when scrolled into view
const CountUp = ({ to, decimals = 0, formatFn, suffix = '', duration = 2 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });
  const count = useMotionValue(0);
  const [display, setDisplay] = useState('0');

  useEffect(() => {
    if (!isInView) return;
    const controls = animate(count, to, {
      duration,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => {
        const val = decimals > 0 ? v.toFixed(decimals) : Math.floor(v);
        setDisplay(formatFn ? formatFn(Number(val)) : String(val));
      },
    });
    return controls.stop;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInView]);

  return <span ref={ref}>{display}{suffix}</span>;
};

// Animation variants — transform/opacity only (no layout shifts)
const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
};
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5, ease: "easeOut" } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.13, delayChildren: 0.15 } },
};

// Hero Section
const HeroSection = () => {
  return (
    <section data-testid="hero-section">
      {/* ── MOBILE: stacked (photo then text) ── */}
      <div className="lg:hidden">
        <div className="relative w-full pt-[72px] overflow-hidden" style={{ height: '90vw', maxHeight: '500px', minHeight: '260px' }}>
          <img
            src="/dr-jain.avif"
            alt="Dr. Vicky Jain — Plastic & Reconstructive Surgeon"
            className="w-full h-full object-cover hero-img-zoom"
            style={{ objectPosition: '50% 10%' }}
            fetchPriority="high"
            loading="eager"
            data-testid="hero-doctor-image"
          />
        </div>
<div className="px-6 py-10 border-t-2 border-gold bg-warm-white">
          <motion.div variants={stagger} initial="hidden" animate="visible">
            <motion.p variants={fadeUp} className="text-sm font-bold tracking-widest text-gold uppercase mb-4">
              Plastic & Reconstructive Surgeon · Mumbai
            </motion.p>
            <motion.p variants={fadeUp} className="font-serif text-2xl text-premium-purple mb-2">Dr. Vicky Jain</motion.p>
<motion.p variants={fadeUp} className="mb-6 text-deep-navy/70" style={{ fontSize: "0.85rem", letterSpacing: "0.05em" }}>
              M.Ch. (Plastic &amp; Cosmetic Surgery) · Board-Certified Surgeon · Cosmetic Surgery Fellowship, Switzerland · Lasers Fellowship, APSI
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-serif text-4xl text-premium-purple leading-[1.08] mb-5">
              Where Surgical Precision Meets Aesthetic Artistry
            </motion.h1>
<motion.p variants={fadeUp} className="text-base text-deep-navy/75 leading-reading mb-8">
              Mumbai's leading specialist in cosmetic and reconstructive surgery.<br />Over 17 years of clinical experience, international training, and natural-looking results tailored to every individual.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col gap-4">
              <Button asChild className="btn-gold border-none rounded-none px-8 py-6 text-base" data-testid="hero-book-consultation">
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                  Book a Consultation <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button asChild variant="outline" className="rounded-none px-8 py-6 text-base border-premium-purple/40 text-premium-purple bg-transparent" data-testid="hero-explore-procedures">
                <a href="#services">Explore Procedures</a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* ── DESKTOP: full-bleed overlay ── */}
      <div className="hidden lg:block relative min-h-[92vh] pt-20 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-no-repeat hero-img-zoom"
          style={{ backgroundImage: `url('/dr-jain.avif')`, backgroundPosition: 'right 80px', backgroundSize: 'auto 110%' }}
        />
        <div className="absolute inset-0 pointer-events-none" style={{
background: 'linear-gradient(to right, rgba(255,255,255,1) 0%, rgba(255,255,255,0.97) 30%, rgba(255,255,255,0.85) 48%, rgba(255,255,255,0.45) 58%, rgba(255,255,255,0.08) 68%, rgba(255,255,255,0) 75%)'
        }} />
        <div className="absolute inset-y-0 pointer-events-none" style={{
          left: '34%', width: '22%',
          backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)',
          maskImage: 'linear-gradient(to right, transparent 0%, black 35%, black 65%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 35%, black 65%, transparent 100%)',
        }} />
        <div className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, transparent, rgba(255,255,255,0.30))' }} />

        <div className="relative z-10 w-full flex items-center min-h-[92vh]">
          <div className="px-12 lg:px-16 max-w-3xl">
            <motion.div className="py-24" variants={stagger} initial="hidden" animate="visible">
              <motion.p variants={fadeUp} className="text-base font-bold tracking-widest text-gold uppercase mb-6">
                Plastic & Reconstructive Surgeon · Mumbai
              </motion.p>
              <motion.p variants={fadeUp} className="font-serif text-3xl text-premium-purple mb-2">Dr. Vicky Jain</motion.p>
<motion.p variants={fadeUp} className="mb-7 text-deep-navy/70" style={{ fontSize: "0.9rem", letterSpacing: "0.05em", fontWeight: 400 }}>
                M.Ch. (Plastic &amp; Cosmetic Surgery) · Board-Certified Surgeon · Cosmetic Surgery Fellowship, Switzerland · Lasers Fellowship, APSI
              </motion.p>
              <motion.h1 variants={fadeUp} className="font-serif text-6xl xl:text-7xl text-premium-purple leading-[1.08] mb-8">
                Where Surgical Precision Meets Aesthetic Artistry
              </motion.h1>
<motion.p variants={fadeUp} className="text-lg text-deep-navy/75 leading-reading mb-10">
                Mumbai's leading specialist in cosmetic and reconstructive surgery.<br />Over 17 years of clinical experience, international training, and natural-looking results tailored to every individual.
              </motion.p>
              <motion.div variants={fadeUp} className="flex flex-row gap-4">
                <Button asChild className="btn-gold border-none rounded-none px-8 py-6 text-base" data-testid="hero-book-consultation">
                  <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                    Book a Consultation <ArrowRight className="w-5 h-5 ml-2" />
                  </a>
                </Button>
                <Button asChild variant="outline" className="rounded-none px-8 py-6 text-base border-premium-purple/40 text-premium-purple bg-transparent hover:bg-premium-purple/10 hover:text-premium-purple" data-testid="hero-explore-procedures">
                  <a href="#services">Explore Procedures</a>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Stats Bar Section
const StatsBar = () => {
  return (
    <section className="bg-warm-white py-12 md:py-16 border-y border-deep-navy/[0.06]" data-testid="stats-section" aria-label="Practice statistics">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12"
          aria-live="polite"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {stats.map((stat, index) => {
            const labelClass = cn(
              "text-xs md:text-sm text-deep-navy/55 leading-snug mx-auto",
              stat.labelNormalCase
                ? "normal-case tracking-wide max-w-[14rem]"
                : "tracking-widest uppercase max-w-[11rem]"
            );
            const inner = (
              <>
                <p className="stat-number text-4xl md:text-5xl lg:text-6xl text-gold mb-2">
                  <CountUp
                    to={stat.countTo}
                    suffix={stat.suffix}
                    decimals={stat.decimals || 0}
                    formatFn={stat.formatFn}
                  />
                </p>
                <p className={labelClass}>{stat.label}</p>
              </>
            );
            if (stat.href) {
              return (
                <motion.a
                  key={index}
                  variants={fadeUp}
                  href={stat.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-center rounded-sm outline-none hover:opacity-95 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-warm-white"
                  data-testid={`stat-${index}`}
                  aria-label={`${stat.label}: ${stat.number} — open Google listing`}
                >
                  {inner}
                </motion.a>
              );
            }
            return (
              <motion.div key={index} variants={fadeUp} className="text-center" data-testid={`stat-${index}`}>
                {inner}
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <section id="services" className="bg-warm-white py-20 md:py-32" data-testid="services-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Section Header */}
        <motion.div
          className="text-center mb-12 md:mb-16 px-2"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.p variants={fadeUp} className="text-sm font-medium tracking-widest text-gold uppercase mb-3 md:mb-4">
            Our Services
          </motion.p>
          <motion.h2 variants={fadeUp} className="font-serif text-3xl md:text-4xl lg:text-5xl text-premium-purple">
            Comprehensive Surgical Care
          </motion.h2>
        </motion.div>

        {/* Service Cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={fadeUp}
              className="service-card bg-white p-8 md:p-10"
              data-testid={`service-card-${index}`}
            >
              <div className="w-14 h-14 rounded-none bg-gold/10 flex items-center justify-center mb-6">
                <service.icon className="w-7 h-7 text-gold" />
              </div>
              <h3 className="font-serif text-xl md:text-2xl text-premium-purple mb-3 md:mb-4 font-semibold tracking-tight">
                {service.title}
              </h3>
              <p className="text-deep-navy/70 leading-reading mb-6 text-[15px] sm:text-base">
                {service.description}
              </p>
              {service.isRoute ? (
                <Link
                  to={service.link}
                  className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
                  data-testid={`service-link-${index}`}
                >
                  Learn More
                  <ChevronRight className="card-arrow w-5 h-5 ml-1 transition-all" />
                </Link>
              ) : (
                <a
                  href={service.link}
                  className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
                  data-testid={`service-link-${index}`}
                >
                  Learn More
                  <ChevronRight className="card-arrow w-5 h-5 ml-1 transition-all" />
                </a>
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Why Us Section
const WhyUsSection = () => {
  return (
    <section id="why-us" className="bg-premium-purple py-20 md:py-32 relative overflow-hidden" data-testid="why-us-section">
      {/* Subtle texture overlay */}
      <div className="absolute inset-0 opacity-5">
        <div className="w-full h-full" style={{ 
          backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} />
      </div>
      
      <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-12 md:mb-16 px-2"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.p variants={fadeUp} className="text-sm font-medium tracking-widest text-gold uppercase mb-3 md:mb-4">
            Why Choose Us
          </motion.p>
          <motion.h2 variants={fadeUp} className="font-serif text-3xl md:text-4xl lg:text-5xl text-warm-white">
            Why Dr. Vicky Jain
          </motion.h2>
        </motion.div>

        {/* 2x2 Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-12 md:gap-y-16"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {whyUsPoints.map((point, index) => (
            <motion.div
              key={index}
              variants={fadeUp}
              className="why-us-card flex gap-6"
              data-testid={`why-us-${index}`}
            >
              <div className="flex-shrink-0">
                <div className="w-14 h-14 rounded-none bg-gold/20 flex items-center justify-center">
                  <point.icon className="why-icon w-7 h-7 text-gold transition-colors" />
                </div>
              </div>
              <div>
                <h3 className="font-serif text-xl md:text-2xl text-warm-white mb-2 md:mb-3 font-semibold tracking-tight">
                  {point.title}
                </h3>
                <p className="text-warm-white/75 leading-reading text-[15px] sm:text-base">
                  {point.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  return (
    <section id="testimonials" className="bg-warm-white pt-20 pb-12 md:pt-32 md:pb-16" data-testid="testimonials-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl min-w-0">
        {/* Section Header */}
        <motion.div
          className="text-center mb-12 md:mb-16 px-2"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.p variants={fadeUp} className="text-sm font-medium tracking-widest text-gold uppercase mb-3 md:mb-4">
            Patient Stories
          </motion.p>
          <motion.h2 variants={fadeUp} className="font-serif text-3xl md:text-4xl lg:text-5xl text-premium-purple">
            What Our Patients Say
          </motion.h2>
          <motion.p variants={fadeUp} className="text-deep-navy/65 text-[15px] sm:text-base max-w-2xl mx-auto mt-3 md:mt-4 leading-reading">
            Verified patient reviews. Scroll to read more.
          </motion.p>
        </motion.div>
        
        {/* Testimonial cards — horizontal scroll, compact copy */}
        <div className="relative -mx-4 md:-mx-8 min-w-0 px-4 md:px-8">
          <TestimonialScrollRow className="pb-2" data-testid="testimonials-scroll">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className="testimonial-card bg-white border border-deep-navy/10 shrink-0 grow-0 snap-start snap-always flex flex-col p-6 sm:p-7 max-sm:basis-[min(88vw,320px)] sm:basis-[calc((100%-3.75rem)/3.5)]"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-gold text-gold" />
                  ))}
                </div>
                <p className="font-serif text-base text-deep-navy italic leading-reading mb-5 flex-1 min-h-0">
                  "{testimonial.quote}"
                </p>
                <div className="pt-4 border-t border-deep-navy/10 mt-auto">
                  <p className="font-semibold text-deep-navy text-sm leading-snug">{testimonial.name}</p>
                  <p className="text-xs sm:text-sm text-deep-navy/70 leading-snug mt-0.5">{testimonial.procedure}</p>
                </div>
              </div>
            ))}
          </TestimonialScrollRow>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  const { pathname } = useLocation();
  return (
  <footer data-testid="footer">
    {/* Pre-footer CTA Band — homepage only */}
    {pathname === "/" && <div className="bg-warm-white border-t border-deep-navy/10">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-16 md:py-20">
        <motion.div
          className="flex flex-col md:flex-row items-center justify-between gap-8"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.div variants={fadeUp} className="text-center md:text-left">
            <p className="text-xs font-medium tracking-widest text-gold uppercase mb-2 sm:mb-3">Begin Your Journey</p>
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-premium-purple mb-2 leading-tight">
              Ready to Transform Your Confidence?
            </h2>
            <p className="text-deep-navy/65 text-[15px] sm:text-base max-w-xl leading-reading">
              Book a private consultation with Dr. Vicky Jain — 17+ years of surgical excellence, Mumbai.
            </p>
          </motion.div>
          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Button asChild className="btn-gold border-none rounded-none px-8 py-5 text-sm">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-4 h-4 mr-2" />WhatsApp Us
              </a>
            </Button>
            <Button asChild variant="outline" className="btn-ghost rounded-none px-8 py-5 text-sm">
              <a href={PHONE_LINK}>
                <Phone className="w-4 h-4 mr-2" />Call Now
              </a>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </div>}

    {/* Main Footer */}
    <div className="bg-premium-purple pt-14 pb-8">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-12 pb-12 border-b border-warm-white/10">

          {/* Brand Column */}
          <div>
            <h3 className="font-serif text-2xl text-warm-white mb-1">Dr. Vicky Jain</h3>
            <p className="text-gold text-xs tracking-widest uppercase mb-4">M.Ch. · Board-Certified Surgeon</p>
            <p className="text-warm-white/55 text-sm leading-relaxed mb-6">
              Mumbai's leading plastic &amp; reconstructive surgeon. Precision surgery, compassionate care, and natural-looking results.
            </p>
            <div className="flex flex-wrap gap-2 mb-8 items-center">
              <span className="text-xs text-gold border border-gold/30 px-3 py-1">17+ Years</span>
              <span className="text-xs text-gold border border-gold/30 px-3 py-1">{FOOTER_PATIENTS_BADGE}</span>
              <a
                href={GOOGLE_MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-google-rating"
                className="inline-flex items-center gap-2 text-xs text-gold border border-gold/30 px-3 py-1.5 hover:border-gold/55 hover:bg-gold/5 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-deep-navy"
                aria-label={`Rated ${GOOGLE_BUSINESS_RATING} on Google — open listing`}
              >
                <GoogleGMark className="h-4 w-4" />
                <span className="normal-case tracking-normal">
                  Rated <span className="font-semibold">{GOOGLE_BUSINESS_RATING}</span> on Google
                </span>
              </a>
            </div>
            <div className="flex gap-3">
              {[
                { label: "Facebook", href: "https://www.facebook.com/the.vickyz", testid: "social-facebook", path: "M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z" },
                { label: "Instagram", href: "https://www.instagram.com/drvickyjainplasticsurgeon/", testid: "social-instagram", path: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" },
                { label: "LinkedIn", href: "https://www.linkedin.com/in/dr-vicky-jain/", testid: "social-linkedin", path: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" },
                { label: "YouTube", href: "https://www.youtube.com/@mumbaiplasticsurgeon-drvicky", testid: "social-youtube", path: "M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" },
              ].map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} data-testid={s.testid}
                  className="w-9 h-9 flex items-center justify-center border border-warm-white/15 text-warm-white/60 hover:border-gold hover:text-gold transition-colors duration-300 cursor-pointer rounded-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-deep-navy">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d={s.path} /></svg>
                </a>
              ))}
            </div>
          </div>

          {/* Links + Contact grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-8">
            {FOOTER_NAV_COLUMNS.map((col, colIndex) => (
              <div key={col.heading}>
                <h4 className="text-xs font-semibold tracking-widest uppercase text-gold mb-5">{col.heading}</h4>
                <ul className="space-y-2.5">
                  {col.links.map((link, index) => (
                    <li key={`${col.heading}-${link.href}-${index}`}>
                      <Link to={link.href}
                        className="footer-link text-warm-white/55 text-sm transition-colors duration-300"
                        data-testid={`footer-col-${colIndex}-link-${index}`}>
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Contact Column */}
            <div>
              <h4 className="text-xs font-semibold tracking-widest uppercase text-gold mb-5">Contact</h4>
              <ul className="space-y-4">
                <li>
                  <a href={PHONE_LINK} className="flex items-start gap-3 group cursor-pointer" data-testid="footer-phone">
                    <Phone className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                    <span className="text-sm text-warm-white/55 group-hover:text-warm-white transition-colors duration-300">+91-9619489446</span>
                  </a>
                </li>
                <li>
                  <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 group cursor-pointer" data-testid="footer-whatsapp">
                    <MessageCircle className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                    <span className="text-sm text-warm-white/55 group-hover:text-warm-white transition-colors duration-300">WhatsApp Chat</span>
                  </a>
                </li>
                <li>
                  <a
                    href={GOOGLE_MAPS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3 group cursor-pointer"
                    data-testid="footer-maps"
                  >
                    <MapPin className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                    <span className="text-sm text-warm-white/55 group-hover:text-warm-white transition-colors duration-300">
                      {CLINIC_ADDRESS_DISPLAY}
                    </span>
                  </a>
                </li>
                <li>
                  <Link to="/contact" className="flex items-start gap-3 group cursor-pointer" data-testid="footer-contact-link">
                    <Mail className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                    <span className="text-sm text-warm-white/55 group-hover:text-warm-white transition-colors duration-300">Send a Message</span>
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-7 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-warm-white/50 text-xs">© {new Date().getFullYear()} Dr. Vicky Jain. All rights reserved.</p>
          <nav className="flex items-center gap-6">
            {[{ name: "About", href: "/about" }, { name: "Blog", href: "/blog" }, { name: "Contact", href: "/contact" }].map((link) => (
              <Link key={link.href} to={link.href} className="text-warm-white/50 text-xs hover:text-warm-white/70 transition-colors duration-300">{link.name}</Link>
            ))}
          </nav>
        </div>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: "Dr. Vicky Jain — Plastic & Reconstructive Surgery",
            url: "https://drvickyjain.com",
            telephone: "+91-9619489446",
            image: "https://drvickyjain.com/dr-jain.avif",
            hasMap: GOOGLE_MAPS_URL,
            address: {
              "@type": "PostalAddress",
              streetAddress: CLINIC_ADDRESS_STREET,
              addressLocality: "Mumbai",
              addressRegion: "Maharashtra",
              postalCode: "400004",
              addressCountry: "IN",
            },
          }),
        }}
      />
    </div>
  </footer>
  );
};


// Floating WhatsApp Button
const FloatingWhatsApp = () => {
  return (
    <a
      href={WHATSAPP_LINK}
      target="_blank"
      rel="noopener noreferrer"
      className="whatsapp-float"
      data-testid="floating-whatsapp"
      aria-label="Chat on WhatsApp"
    >
      <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    </a>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);
  return null;
};

// Main App Component
function App() {
  return (
    <MotionConfig reducedMotion="user">
    <div className="App bg-warm-white">
      <BrowserRouter>
        <ScrollToTop />
        <Navbar />
        <main id="main-content">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/rhinoplasty-in-mumbai" element={<RhinoplastyPage />} />
            <Route path="/rhinoplasty-nose-surgery-mumbai" element={<RhinoplastyPage />} />
            <Route path="/procedures/rhinoplasty" element={<Navigate to="/rhinoplasty-in-mumbai" replace />} />
            <Route path="/chin-augmentation-in-mumbai" element={<ChinAugmentationPage />} />
            <Route path="/chin-augmentation-mumbai" element={<Navigate to="/chin-augmentation-in-mumbai" replace />} />
            <Route path="/procedures/chin-augmentation" element={<Navigate to="/chin-augmentation-in-mumbai" replace />} />
            <Route path="/cheek-augmentation-in-mumbai" element={<CheekAugmentationPage />} />
            <Route path="/cheek-augmentation-mumbai" element={<Navigate to="/cheek-augmentation-in-mumbai" replace />} />
            <Route path="/procedures/cheek-augmentation" element={<Navigate to="/cheek-augmentation-in-mumbai" replace />} />
            <Route path="/breast-implant-surgery-mumbai" element={<BreastAugmentationPage />} />
            <Route path="/breast-implant-surgery-in-mumbai" element={<Navigate to="/breast-implant-surgery-mumbai" replace />} />
            <Route path="/breast-augmentation-in-mumbai" element={<Navigate to="/breast-implant-surgery-mumbai" replace />} />
            <Route path="/procedures/breast-augmentation" element={<Navigate to="/breast-implant-surgery-mumbai" replace />} />
            <Route path="/breast-lift-surgery-mumbai" element={<BreastLiftPage />} />
            <Route path="/breast-lift-surgery-in-mumbai" element={<Navigate to="/breast-lift-surgery-mumbai" replace />} />
            <Route path="/procedures/breast-lift" element={<Navigate to="/breast-lift-surgery-mumbai" replace />} />
            <Route path="/breast-reduction-surgery-mumbai" element={<BreastReductionPage />} />
            <Route path="/breast-reduction-surgery-in-mumbai" element={<Navigate to="/breast-reduction-surgery-mumbai" replace />} />
            <Route path="/procedures/breast-reduction" element={<Navigate to="/breast-reduction-surgery-mumbai" replace />} />
            <Route path="/breast-fat-transfer-lipofilling-mumbai" element={<BreastFatTransferPage />} />
            <Route path="/breast-implant-removal-surgery-mumbai" element={<BreastImplantRemovalPage />} />
            <Route path="/fibroadenoma-breast-lump-treatment-mumbai" element={<FibroadenomaPage />} />
            <Route path="/lipoma-removal-surgery-mumbai" element={<LipomaRemovalPage />} />
            <Route path="/sebaceous-cyst-removal-mumbai" element={<SebaceousCystRemovalPage />} />
            <Route path="/mole-removal-mumbai" element={<MoleRemovalPage />} />
            <Route path="/corn-removal-treatment-mumbai" element={<CornRemovalPage />} />
            <Route path="/keloid-treatment-mumbai" element={<KeloidTreatmentPage />} />
            <Route path="/scar-management-treatment-mumbai" element={<ScarManagementPage />} />
            <Route path="/skin-tag-removal-mumbai" element={<SkinTagRemovalPage />} />
            <Route path="/wart-removal-mumbai" element={<WartRemovalPage />} />
            <Route path="/nail-removal-surgery-mumbai" element={<NailRemovalSurgeryPage />} />
            <Route path="/neurofibroma-removal-mumbai" element={<NeurofibromaRemovalPage />} />
            <Route path="/axillary-breast-removal-mumbai" element={<AxillaryBreastRemovalPage />} />
            <Route path="/male-nipple-reduction-mumbai" element={<MaleNippleReductionPage />} />
            <Route path="/inverted-nipple-correction-mumbai" element={<InvertedNippleCorrectionPage />} />
            <Route path="/nipple-correction-surgery-mumbai" element={<Navigate to="/inverted-nipple-correction-mumbai" replace />} />
            <Route path="/nipple-correction-surgery-in-mumbai" element={<Navigate to="/inverted-nipple-correction-mumbai" replace />} />
            <Route path="/procedures/nipple-correction" element={<Navigate to="/inverted-nipple-correction-mumbai" replace />} />
            <Route path="/liposuction-surgery-mumbai" element={<LiposuctionPage />} />
            <Route path="/liposuction-in-mumbai" element={<Navigate to="/liposuction-surgery-mumbai" replace />} />
            <Route path="/procedures/liposuction" element={<Navigate to="/liposuction-surgery-mumbai" replace />} />
            <Route path="/tummy-tuck-surgery-mumbai" element={<TummyTuckPage />} />
            <Route path="/tummy-tuck-in-mumbai" element={<Navigate to="/tummy-tuck-surgery-mumbai" replace />} />
            <Route path="/procedures/tummy-tuck" element={<Navigate to="/tummy-tuck-surgery-mumbai" replace />} />
            <Route path="/arm-lift-surgery-mumbai" element={<ArmLiftPage />} />
            <Route path="/arm-lift-in-mumbai" element={<Navigate to="/arm-lift-surgery-mumbai" replace />} />
            <Route path="/procedures/arm-lift" element={<Navigate to="/arm-lift-surgery-mumbai" replace />} />
            <Route path="/procedures/brachioplasty" element={<Navigate to="/arm-lift-surgery-mumbai" replace />} />
            <Route path="/thigh-lift-surgery-mumbai" element={<ThighLiftPage />} />
            <Route path="/thigh-lift-in-mumbai" element={<Navigate to="/thigh-lift-surgery-mumbai" replace />} />
            <Route path="/procedures/thigh-lift" element={<Navigate to="/thigh-lift-surgery-mumbai" replace />} />
            <Route path="/procedures/thighplasty" element={<Navigate to="/thigh-lift-surgery-mumbai" replace />} />
            <Route path="/buttock-augmentation-surgery-mumbai" element={<ButtockAugmentationPage />} />
            <Route path="/buttock-augmentation-in-mumbai" element={<Navigate to="/buttock-augmentation-surgery-mumbai" replace />} />
            <Route path="/procedures/buttock-augmentation" element={<Navigate to="/buttock-augmentation-surgery-mumbai" replace />} />
            <Route path="/procedures/brazilian-butt-lift" element={<Navigate to="/buttock-augmentation-surgery-mumbai" replace />} />
            <Route path="/procedures/bbl" element={<Navigate to="/buttock-augmentation-surgery-mumbai" replace />} />
            <Route path="/body-lift-surgery-mumbai" element={<BodyLiftPage />} />
            <Route path="/body-lift-in-mumbai" element={<Navigate to="/body-lift-surgery-mumbai" replace />} />
            <Route path="/procedures/body-lift" element={<Navigate to="/body-lift-surgery-mumbai" replace />} />
            <Route path="/procedures/lower-body-lift" element={<Navigate to="/body-lift-surgery-mumbai" replace />} />
            <Route path="/procedures/belt-lipectomy" element={<Navigate to="/body-lift-surgery-mumbai" replace />} />
            <Route path="/facelift-surgery-mumbai" element={<FaceliftPage />} />
            <Route path="/facelift-surgery-in-mumbai" element={<Navigate to="/facelift-surgery-mumbai" replace />} />
            <Route path="/procedures/facelift" element={<Navigate to="/facelift-surgery-mumbai" replace />} />
            <Route path="/blepharoplasty-eyelid-surgery-mumbai" element={<BlepharoplastyPage />} />
            <Route path="/procedures/blepharoplasty" element={<Navigate to="/blepharoplasty-eyelid-surgery-mumbai" replace />} />
            <Route path="/thread-lift-mumbai" element={<ThreadLiftPage />} />
            <Route path="/non-surgical/thread-lift" element={<Navigate to="/thread-lift-mumbai" replace />} />
            <Route path="/dimple-creation-surgery-mumbai" element={<DimpleplastyPage />} />
            <Route path="/procedures/dimpleplasty" element={<Navigate to="/dimple-creation-surgery-mumbai" replace />} />
            <Route path="/buccal-fat-removal-mumbai" element={<BuccalFatRemovalPage />} />
            <Route path="/buccal-fat-removal-in-mumbai" element={<Navigate to="/buccal-fat-removal-mumbai" replace />} />
            <Route path="/procedures/buccal-fat-removal" element={<Navigate to="/buccal-fat-removal-mumbai" replace />} />
            <Route path="/neck-lift-surgery-mumbai" element={<NeckLiftPage />} />
            <Route path="/neck-lift-surgery-in-mumbai" element={<Navigate to="/neck-lift-surgery-mumbai" replace />} />
            <Route path="/procedures/neck-lift" element={<Navigate to="/neck-lift-surgery-mumbai" replace />} />
            <Route path="/double-chin-removal-mumbai" element={<DoubleChinRemovalPage />} />
            <Route path="/otoplasty-ear-reshaping-surgery-mumbai" element={<OtoplastyPage />} />
            <Route path="/otoplasty-ear-reshaping-surgery-in-mumbai" element={<Navigate to="/otoplasty-ear-reshaping-surgery-mumbai" replace />} />
            <Route path="/otoplasty-in-mumbai" element={<Navigate to="/otoplasty-ear-reshaping-surgery-mumbai" replace />} />
            <Route path="/otoplasty-mumbai" element={<Navigate to="/otoplasty-ear-reshaping-surgery-mumbai" replace />} />
            <Route path="/ear-reshaping-surgery-mumbai" element={<Navigate to="/otoplasty-ear-reshaping-surgery-mumbai" replace />} />
            <Route path="/ear-reshaping-surgery-in-mumbai" element={<Navigate to="/otoplasty-ear-reshaping-surgery-mumbai" replace />} />
            <Route path="/procedures/otoplasty" element={<Navigate to="/otoplasty-ear-reshaping-surgery-mumbai" replace />} />
            <Route path="/earlobe-repair-surgery-mumbai" element={<EarlobeRepairPage />} />
            <Route path="/gynecomastia-surgery-mumbai" element={<GynecomastiaPage />} />
            <Route path="/gynecomastia-surgery-in-mumbai" element={<Navigate to="/gynecomastia-surgery-mumbai" replace />} />
            <Route path="/procedures/gynecomastia" element={<Navigate to="/gynecomastia-surgery-mumbai" replace />} />
            <Route path="/mommy-makeover-surgery-mumbai" element={<MommyMakeoverPage />} />
            <Route path="/mommy-makeover-in-mumbai" element={<Navigate to="/mommy-makeover-surgery-mumbai" replace />} />
            <Route path="/procedures/mommy-makeover" element={<Navigate to="/mommy-makeover-surgery-mumbai" replace />} />
            <Route path="/cosmetic-surgery-in-mumbai" element={<CosmeticSurgeryPage />} />
            <Route path="/cosmetic-surgery" element={<Navigate to="/cosmetic-surgery-in-mumbai" replace />} />
            <Route path="/reconstructive-surgery-in-mumbai" element={<ReconstructiveSurgeryPage />} />
            <Route path="/reconstructive-surgery" element={<Navigate to="/reconstructive-surgery-in-mumbai" replace />} />
            <Route path="/non-surgical-treatments-in-mumbai" element={<NonSurgicalPage />} />
            <Route path="/non-surgical" element={<Navigate to="/non-surgical-treatments-in-mumbai" replace />} />
            <Route path="/cleft-lip-palate-surgery-in-mumbai" element={<CleftLipPalatePage />} />
            <Route path="/reconstructive/cleft-lip-palate" element={<Navigate to="/cleft-lip-palate-surgery-in-mumbai" replace />} />
            <Route path="/craniosynostosis-surgery-in-mumbai" element={<CraniosynostosisPage />} />
            <Route path="/reconstructive/craniosynostosis" element={<Navigate to="/craniosynostosis-surgery-in-mumbai" replace />} />
            <Route path="/ear-deformity-correction-in-mumbai" element={<EarDeformitiesPage />} />
            <Route path="/reconstructive/ear-deformities" element={<Navigate to="/ear-deformity-correction-in-mumbai" replace />} />
            <Route path="/skin-grafting-surgery-mumbai" element={<SkinGraftingPage />} />
            <Route path="/burns-burn-scar-treatment-mumbai" element={<BurnReconstructionPage />} />
            <Route path="/burn-reconstruction-surgery-in-mumbai" element={<Navigate to="/burns-burn-scar-treatment-mumbai" replace />} />
            <Route path="/reconstructive/burn-reconstruction" element={<Navigate to="/burns-burn-scar-treatment-mumbai" replace />} />
            <Route path="/post-burn-contracture-surgery-in-mumbai" element={<PostBurnContracturePage />} />
            <Route path="/reconstructive/post-burn-contracture" element={<Navigate to="/post-burn-contracture-surgery-in-mumbai" replace />} />
            <Route path="/hypertrophic-scar-treatment-in-mumbai" element={<HypertrophicScarsPage />} />
            <Route path="/reconstructive/hypertrophic-scars" element={<Navigate to="/hypertrophic-scar-treatment-in-mumbai" replace />} />
            <Route path="/limb-salvage-surgery-in-mumbai" element={<LimbSalvagePage />} />
            <Route path="/reconstructive/limb-salvage" element={<Navigate to="/limb-salvage-surgery-in-mumbai" replace />} />
            <Route path="/replantation-surgery-in-mumbai" element={<ReplantationPage />} />
            <Route path="/reconstructive/replantation" element={<Navigate to="/replantation-surgery-in-mumbai" replace />} />
            <Route path="/diabetic-foot-surgery-in-mumbai" element={<DiabeticFootPage />} />
            <Route path="/reconstructive/diabetic-foot" element={<Navigate to="/diabetic-foot-surgery-in-mumbai" replace />} />
            <Route path="/migraine-surgery-in-mumbai" element={<MigraineSurgeryPage />} />
            <Route path="/reconstructive/migraine-surgery" element={<Navigate to="/migraine-surgery-in-mumbai" replace />} />
            <Route path="/dermal-fillers-mumbai" element={<DermalFillersPage />} />
            <Route path="/dermal-fillers-in-mumbai" element={<Navigate to="/dermal-fillers-mumbai" replace />} />
            <Route path="/non-surgical/dermal-fillers" element={<Navigate to="/dermal-fillers-mumbai" replace />} />
            <Route path="/procedures/dermal-fillers" element={<Navigate to="/dermal-fillers-mumbai" replace />} />
            <Route path="/liquid-rhinoplasty-mumbai" element={<LiquidRhinoplastyPage />} />
            <Route path="/botox-treatment-mumbai" element={<BotoxTreatmentPage />} />
            <Route path="/botox-fillers-in-mumbai" element={<Navigate to="/botox-treatment-mumbai" replace />} />
            <Route path="/non-surgical/botox-fillers" element={<Navigate to="/botox-treatment-mumbai" replace />} />
            <Route path="/chemical-peels-mumbai" element={<ChemicalPeelPage />} />
            <Route path="/chemical-peel-in-mumbai" element={<Navigate to="/chemical-peels-mumbai" replace />} />
            <Route path="/non-surgical/chemical-peel" element={<Navigate to="/chemical-peels-mumbai" replace />} />
            <Route path="/prp-therapy-hair-loss-mumbai" element={<PRPTherapyPage />} />
            <Route path="/prp-therapy-in-mumbai" element={<Navigate to="/prp-therapy-hair-loss-mumbai" replace />} />
            <Route path="/non-surgical/prp-therapy" element={<Navigate to="/prp-therapy-hair-loss-mumbai" replace />} />
            <Route path="/spider-vein-treatment-in-mumbai" element={<SpiderVeinPage />} />
            <Route path="/non-surgical/spider-vein" element={<Navigate to="/spider-vein-treatment-in-mumbai" replace />} />
            <Route path="/hair-transplant-mumbai" element={<HairTransplantPage />} />
            <Route path="/hair-transplant-in-mumbai" element={<Navigate to="/hair-transplant-mumbai" replace />} />
            <Route path="/non-surgical/hair-transplant" element={<Navigate to="/hair-transplant-mumbai" replace />} />
            <Route path="/beard-transplant-mumbai" element={<BeardTransplantPage />} />
            <Route path="/hair-fall-hair-loss-treatment-mumbai" element={<HairFallHairLossPage />} />
            <Route path="/hair-loss-treatment-mumbai" element={<Navigate to="/hair-fall-hair-loss-treatment-mumbai" replace />} />
            <Route path="/non-surgical/hair-loss" element={<Navigate to="/hair-fall-hair-loss-treatment-mumbai" replace />} />
            <Route path="/scalp-reduction-in-mumbai" element={<ScalpReductionPage />} />
            <Route path="/non-surgical/scalp-reduction" element={<Navigate to="/scalp-reduction-in-mumbai" replace />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogArticlePage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
        <Footer />
        <FloatingWhatsApp />
      </BrowserRouter>
    </div>
    </MotionConfig>
  );
}

const HomeBlogSection = () => {
  const featured = blogPosts.slice(0, 3);
  return (
    <section className="bg-warm-white py-16 md:py-20 px-4 md:px-8 border-t border-deep-navy/[0.06]">
      <div className="container mx-auto max-w-7xl">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-xs font-medium tracking-widest text-gold uppercase mb-2">From the Blog</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Expert Insights</h2>
          </div>
          <Link to="/blog" className="text-sm font-medium text-gold hover:text-deep-navy transition-colors hidden sm:block">
            All Articles →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {featured.map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="group bg-white border border-deep-navy/10 p-6 hover:border-gold/40 transition-colors"
            >
              <p className="text-xs font-medium tracking-widest text-gold uppercase mb-3">{post.category}</p>
              <h3 className="font-serif text-lg text-premium-purple leading-snug mb-3 group-hover:text-gold transition-colors">
                {post.title}
              </h3>
              <p className="text-deep-navy/60 text-sm leading-relaxed mb-4 line-clamp-3">{post.excerpt}</p>
              <span className="text-xs text-deep-navy/40">{post.date} &middot; {post.readTime}</span>
            </Link>
          ))}
        </div>
        <div className="mt-6 sm:hidden">
          <Link to="/blog" className="text-sm font-medium text-gold hover:text-deep-navy transition-colors">
            All Articles →
          </Link>
        </div>
      </div>
    </section>
  );
};

// Homepage Component
const HomePage = () => {
  const schemaData = {
    "@context": "https://schema.org",
    "@type": ["Physician", "MedicalBusiness"],
    "@id": "https://drvickyjain.com/#physician",
    "name": "Dr. Vicky Jain",
    "url": "https://drvickyjain.com",
    "image": "https://drvickyjain.com/dr-jain.avif",
    "logo": "https://drvickyjain.com/favicon.svg",
    "description": "Board-certified plastic and reconstructive surgeon in Mumbai with 17+ years of clinical experience. Specialising in cosmetic and reconstructive surgery.",
    "medicalSpecialty": ["PlasticSurgery", "CosmeticSurgery"],
    "hasCredential": [
      "M.Ch. (Plastic & Cosmetic Surgery)",
      "Board-Certified Surgeon",
      "Fellowship in Cosmetic Surgery, Switzerland",
      "Fellowship in Lasers, APSI",
    ],
    "address": {
      "@type": "PostalAddress",
      "streetAddress": CLINIC_ADDRESS_STREET,
      "addressLocality": "Mumbai",
      "addressRegion": "Maharashtra",
      "postalCode": "400004",
      "addressCountry": "IN",
    },
    "telephone": PHONE_NUMBER,
    "priceRange": "$$$",
    "areaServed": {
      "@type": "City",
      "name": "Mumbai",
    },
    "sameAs": [GOOGLE_MAPS_URL],
  };
  return (
    <>
      <SEO
        title="Plastic & Reconstructive Surgeon"
        description="Dr. Vicky Jain – Board-certified plastic and reconstructive surgeon. Over 17 years of experience in rhinoplasty, facelift, breast augmentation, liposuction, and reconstructive surgery."
        canonical="/"
        jsonLd={schemaData}
      />
      <HeroSection />
      <StatsBar />
      <ServicesSection />
      <WhyUsSection />
      <TestimonialsSection />
      <HomeBlogSection />
    </>
  );
};

export default App;
